import React from 'react';
import { Link } from 'react-router-dom';

const About: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="mb-8">
            <Link to="/" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
              ← Back to Home
            </Link>
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-8">About Flash AI</h1>

          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Our Mission</h2>
              <p className="text-gray-700 mb-4">
                Flash AI is revolutionizing business continuity planning by making it accessible, 
                efficient, and resilient. We believe every organization, regardless of size, should 
                have access to world-class business continuity planning tools powered by artificial intelligence.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">What We Do</h2>
              <p className="text-gray-700 mb-4">
                We provide an AI-powered platform that helps organizations create comprehensive, 
                Business Continuity Plans aligned with ISO 22301:2019 in a fraction of the time traditionally required. 
                Our platform combines cutting-edge artificial intelligence with deep expertise in business 
                continuity management to deliver:
              </p>
              
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Automated BCP generation tailored to your organization</li>
                <li>Risk assessment and impact analysis tools</li>
                <li>Readiness monitoring and reporting</li>
                <li>Tabletop exercise planning and execution</li>
                <li>Executive training</li>
                <li>Real-time collaboration and document management</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Our Story</h2>
              <p className="text-gray-700 mb-4">
                Flash AI was founded in 2023 by a team of business continuity experts and AI researchers 
                who recognized that traditional BCP development was too slow, expensive, and often resulted 
                in generic, ineffective plans. We set out to change that.
              </p>
              
              <p className="text-gray-700 mb-4">
                Our founders have decades of combined experience in disaster recovery, risk management, 
                and organizational resilience. They've seen firsthand how critical business continuity 
                planning is, and how many organizations struggle to create and maintain effective plans.
              </p>
              
              <p className="text-gray-700 mb-4">
                By combining this expertise with the latest advances in artificial intelligence, we've 
                created a platform that democratizes access to world-class business continuity planning.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Our Technology</h2>
              <p className="text-gray-700 mb-4">
                Flash AI leverages state-of-the-art artificial intelligence and machine learning 
                technologies to understand your organization's unique characteristics and generate 
                tailored business continuity plans. Our platform:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">Natural Language Processing</h3>
                  <p className="text-blue-800 text-sm">
                    Advanced NLP algorithms analyze your business information and generate 
                    human-readable, comprehensive plans.
                  </p>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-green-900 mb-2">Machine Learning</h3>
                  <p className="text-green-800 text-sm">
                    ML models learn from thousands of successful BCPs to provide 
                    industry-specific recommendations and best practices.
                  </p>
                </div>
                
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-purple-900 mb-2">Risk Assessment AI</h3>
                  <p className="text-purple-800 text-sm">
                    Intelligent risk analysis that identifies potential threats and 
                    calculates business impact with unprecedented accuracy.
                  </p>
                </div>
                
                <div className="bg-orange-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-orange-900 mb-2">Compliance Engine</h3>
                  <p className="text-orange-800 text-sm">
                    Automated compliance checking ensures your plans meet ISO 22301 
                    and other relevant standards.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Our Values</h2>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Excellence</h3>
                    <p className="text-gray-700">
                      We strive for the highest quality in everything we do, from our AI algorithms 
                      to our customer service.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Innovation</h3>
                    <p className="text-gray-700">
                      We continuously push the boundaries of what's possible in business 
                      continuity planning through technology.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-purple-600 font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Accessibility</h3>
                    <p className="text-gray-700">
                      We believe that effective business continuity planning should be 
                      accessible to organizations of all sizes.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                    <span className="text-orange-600 font-bold">4</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Trust</h3>
                    <p className="text-gray-700">
                      We build trust through transparency, security, and reliability in 
                      everything we do.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Our Impact</h2>
              <p className="text-gray-700 mb-4">
                Since our launch, Flash AI has helped hundreds of organizations across various 
                industries create and maintain effective business continuity plans. Our clients 
                have reported:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">90%</div>
                  <p className="text-gray-700 text-sm">Reduction in BCP creation time</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">100%</div>
                  <p className="text-gray-700 text-sm">ISO 22301 compliance rate</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">95%</div>
                  <p className="text-gray-700 text-sm">Client satisfaction rate</p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Our Team</h2>
              <p className="text-gray-700 mb-4">
                Our team consists of experts in business continuity, artificial intelligence, 
                software engineering, and customer success. We're passionate about helping 
                organizations build resilience and prepare for the unexpected.
              </p>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Leadership Team</h3>
                <ul className="text-gray-700 space-y-1">
                  <li><strong>CEO:</strong> Dr. Sarah Chen - Former BC consultant with 15+ years experience</li>
                  <li><strong>CTO:</strong> Michael Rodriguez - AI researcher and former Google engineer</li>
                  <li><strong>Head of Product:</strong> Jennifer Kim - Former McKinsey consultant</li>
                  <li><strong>Head of Security:</strong> David Thompson - Former NSA cybersecurity expert</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Security</h2>
              <p className="text-gray-700 mb-4">
                Flash AI does not claim SOC 2, ISO, GDPR, or HIPAA certification.
              </p>
              
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Aligned with ISO/IEC 27001:2022. Alignment is not a certification.</li>
                <li>Designed with GDPR in mind</li>
                <li>Third-party security reviews</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-700 mb-4">
                We'd love to hear from you. Whether you have questions about our platform, 
                need support, or want to discuss how Flash AI can help your organization, 
                we're here to help.
              </p>
              
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">Get in Touch</h3>
                <p className="text-blue-800">
                  <strong>General Inquiries:</strong> hello@flashai.com<br />
                  <strong>Sales:</strong> sales@flashai.com<br />
                  <strong>Support:</strong> support@flashai.com<br />
                  <strong>Address:</strong> 123 Business Street, Suite 100, City, State 12345<br />
                  <strong>Phone:</strong> +1 (555) 123-4567
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
